<template>
  <CollapsableSection
    data-test="classification"
    :title="t('classification_quality_labeling')"
    section-classes="w-full text-xs text-gray-800 border-b border-gray-200 border-solid last:border-0"
    title-classes="lg:text-xl"
    :is-open="isOpen"
    collapsable-padding="px-8 xl:px-12 py-4"
    collapsable-content-classes="px-8 xl:px-12 py-6"
    @update:open="(value) => emit('update:open', value)"
  >
    <div class="w-full md:grid md:grid-cols-2 md:gap-x-4 lg:gap-x-6">
      <ul v-for="(section, index) in sections" :key="index" class="w-full">
        <li
          v-for="classification in section"
          :key="classification.id"
          class="flex items-center mb-2 cursor-pointer"
          tabindex="0"
          @click="openImageModal(classification)"
          @keyup.enter="openImageModal(classification)"
        >
          <div class="w-20 h-10 border border-gray-300 border-solid">
            <CImage
              v-if="classification.assetUrl"
              class="classification-image"
              :src="classification.assetUrl"
              :alt="`${t('logotype_for')} ${classification.classificationName}`"
              width="78"
              height="38"
              :params="{ w: '78', h: '38', fit: 'contain' }"
            />
          </div>
          <span class="ml-2 text-gray-800">{{ classification.classificationName }}</span>
        </li>
      </ul>
    </div>
    <ImageModal v-if="imageModalVariant" :variant="imageModalVariant" @close="imageModalVariant = undefined" />
  </CollapsableSection>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

type Classification = { assetUrl: string; name: string; classificationName: string; id: string };

type Props = {
  isOpen?: boolean;
  classifications?: Classification[];
};

const emit = defineEmits<{
  (e: 'update:open', v: boolean): void;
}>();

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  classifications: () => [],
});

const { t } = useI18n();
const { mq } = useMediaQueries();

const imageModalVariant = ref<undefined | { images: string; name: string }>(undefined);

const isMultiSection = computed(() => {
  if (['xsmall', 'small'].includes(mq.value?.current || '')) return false;

  return props.classifications.length > 7;
});

const sections = computed(() => {
  if (!isMultiSection.value) return [props.classifications];
  const classifications = props.classifications;
  const half = Math.ceil(classifications.length / 2);
  return [classifications.slice(0, half), classifications.slice(half)];
});

const openImageModal = (classification: Classification) => {
  const payload = {
    images: classification.assetUrl,
    name: classification.name,
  };
  imageModalVariant.value = payload;
};
</script>

<script lang="ts">
export default defineComponent({
  name: 'ProductDetailsPageClassificationsSection',
});
</script>

<style lang="scss" scoped>
.classification-image :deep(img) {
  @apply mx-auto;
}
</style>
